import React, { useEffect, useState } from 'react';
import {
  Box,
  MuiBox,
  Drawer,
  Typography,
  Button,
  SvgIconButton,
  FlexBox,
  InputField,
  Card,
  Grid,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { useSelector } from 'react-redux';
import { actions as kioskActions, selectors as kioskSelectors, Kiosk } from 'store/kiosks';
import { useStyles } from '../styled';

export interface KioskRestockDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  kiosk: Kiosk;
}

export const KioskRestockDrawer = (props: KioskRestockDrawerProps) => {
  const { isOpen, onClose, kiosk } = props;

  const { updateKiosk, logEvent, resetAction } = useActions({
    updateKiosk: kioskActions.updateKiosk,
    logEvent: kioskActions.logEvent,
    resetAction: kioskActions.resetAction
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const { success } = useSelector(kioskSelectors.selectAction);

  const [stockValues, setStockValues] = useState<{ [key: string]: number }>({});

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (isOpen && success) {
      onClose();
      resetAction();
    }
    setStockValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, success]);

  const validateAndSetStockValues = (value: string, kioskHardwareId: string) => {
    if (value && !isNaN(Number(value)) && Number(value) >= 0) {
      setStockValues((prevState) => ({
        ...prevState,
        [kioskHardwareId]: Math.abs(Number(value))
      }));
    } else if (!value) {
      setStockValues((prevState) => {
        const { [kioskHardwareId]: _, ...rest } = prevState;
        return rest;
      });
    }
  };

  // on click data entry overlay
  const onClickSave = () => {
    updateKiosk({
      id: kiosk.id,
      hardware: Object.entries(stockValues).map(([kioskHardwareId, currentStock]) => ({
        id: kioskHardwareId,
        values: { currentStock }
      }))
    });

    // kioskId needs to be replaced with exact data.
    logEvent({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
  };

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} hasBorderRadius>
        <Grid container className={classes.kioskRestockDetailsOverlay}>
          <Card fullHeight>
            <Grid className={classes.kioskOverlayHeaderBlock}>
              <Grid className={classes.kioskOverlayHeading}>
                <FlexBox>
                  <Typography className={classes.kioskRestockHeading} sx={{ fontWeight: 600 }}>
                    Load Ticket Stock
                  </Typography>
                </FlexBox>
              </Grid>
              <Grid className={classes.kioskOverlayCloseIcon}>
                <SvgIconButton appearance="transparent" onClick={onClose} data-testid="kiosk-restock-x">
                  <SvgIcon name="exit_circle" size={40} />
                </SvgIconButton>
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography fontWeight={600} fontSize={16}>
                {kiosk.name}
              </Typography>
            </Box>
            <MuiBox className={classes.kioskRestockBox}>
              {kiosk?.hardware
                ?.filter((h) => h.type === 'printer')
                ?.map((printer, idx) => (
                  <FlexBox justify="between" padding="0 0 6" width="350px" key={`printer-restock-${idx}`}>
                    <FlexBox align="center">
                      <Typography className={classes.restockPrinterLabel}>
                        {printer.configuration.displayName || ''} Printer
                      </Typography>
                    </FlexBox>
                    <FlexBox align="center" height="50px">
                      <InputField
                        type="text"
                        size="medium"
                        align="center"
                        sx={{ width: '150px', minWidth: '150px', height: '50px' }}
                        placeholder={(printer.values.currentStock || 0).toString()}
                        value={stockValues.hasOwnProperty(printer.id) ? stockValues[printer.id] : ''}
                        onChange={(e) => validateAndSetStockValues(e.target.value, printer.id)}
                        data-testid={`restock-input-${idx}`}
                      />
                    </FlexBox>
                  </FlexBox>
                ))}
              {/* Save button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button
                  customSize="small"
                  appearance="primary"
                  onClick={onClickSave}
                  className={classes.saveButton}
                  disabled={Object.keys(stockValues).length === 0}
                >
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
